import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import ReCAPTCHA from 'react-google-recaptcha';
import './PublicScanFormPage.scss';
import { requestPublicScan } from './services/PublicScanService';
import { DOMAIN_REGEX } from '../../shared/utils/regex';
import { cleanDomain } from '../../shared/utils/utils';
import { REACT_APP_IS_DEBUG, REACT_APP_RECAPTCHA_SITE_KEY } from '../../shared/utils/config';

interface ScanFormData {
  email: string;
  domain: string;
}

const PublicScanFormPage: React.FC = () => {
  const { t } = useTranslation();
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ScanFormData>();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  useEffect(() => {
    document.body.classList.add('visible', 'white');
  }, []);

  const onSubmit = async (formData: ScanFormData) => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setApiError(null);

    try {
      const recaptchaToken = REACT_APP_IS_DEBUG
        ? ''
        : (await recaptchaRef.current?.executeAsync()) ?? '';

      await requestPublicScan({
        email: formData.email.trim(),
        domain: cleanDomain(formData.domain).trim(),
        recaptchaToken,
      });
      setIsSent(true);
    } catch (error: any) {
      setApiError(
        error?.response?.status === 429 ? t('scanTooManyRequests') : t('scanRequestFailed')
      );
    } finally {
      recaptchaRef.current?.reset();
      setIsSubmitting(false);
    }
  };

  return (
    <div className="public-scan-form">
      <div className="public-scan-form__box">
        <div className="logo">
          <div className="logo-box" />
        </div>

        {isSent ? (
          <div className="public-scan-form__sent">
            <span className="material-symbols-outlined public-scan-form__sent-icon">mark_email_read</span>
            <h1>{t('scanEmailSentTitle')}</h1>
            <p>{t('scanEmailSentDesc')}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="public-scan-form__form">
            <h1>{t('scanFormTitle')}</h1>
            <p className="public-scan-form__desc">{t('scanFormDesc')}</p>

            <div className="public-scan-form__field">
              <label className="public-scan-form__label" htmlFor="scan-domain">
                {t('domain')}
              </label>
              <InputText
                id="scan-domain"
                {...register('domain', {
                  required: t('fieldIsRequired'),
                  pattern: { value: DOMAIN_REGEX, message: t('wrongDomain') },
                })}
                placeholder="example.com"
                className={`input--xxl ${errors.domain ? 'form-box__invalid' : ''}`}
              />
              {errors.domain && (
                <span className="public-scan-form__error">{errors.domain.message}</span>
              )}
            </div>

            <div className="public-scan-form__field">
              <label className="public-scan-form__label" htmlFor="scan-email">
                {t('emailAddress')}
              </label>
              <InputText
                id="scan-email"
                {...register('email', { required: t('fieldIsRequired') })}
                type="email"
                placeholder="you@company.com"
                className={`input--xxl ${errors.email ? 'form-box__invalid' : ''}`}
              />
              {errors.email && (
                <span className="public-scan-form__error">{errors.email.message}</span>
              )}
            </div>

            {apiError && <div className="public-scan-form__error">{apiError}</div>}

            <Button className="btn btn--xxl" type="submit" disabled={isSubmitting}>
              {t('scanFormSubmit')}
            </Button>
            <p className="public-scan-form__note">{t('scanFormNote')}</p>
          </form>
        )}
      </div>

      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={REACT_APP_RECAPTCHA_SITE_KEY || 'sample'}
        size="invisible"
      />
    </div>
  );
};

export default PublicScanFormPage;
